import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import NodeDataModal, { NodeDataTarget } from './NodeDataModal';
import { showAlert } from '../../../common/AppDialog';
import { be1Fetch } from '../../../../config/api';
import { errorText } from '../../../../services/errorText';
import { withTimeZone } from '../../../../utils/timeZone';
import TransformLoadPanel from './TransformLoadPanel';
import RdbDataPanel from './RdbDataPanel';
import SamFilePanel from './SamFilePanel';
import SplitGroupPanel from './SplitGroupPanel';
import ExecNodePanel from './ExecNodePanel';
import SubflowPanel from './SubflowPanel';
import AnalyticsNodePanel from './AnalyticsNodePanel';
import QualityNodePanel from './QualityNodePanel';
import CompareNodePanel from './CompareNodePanel';
import InnerDataPanel from './InnerDataPanel';
import ControlNodePanel from './ControlNodePanel';

/**
 * 노드 속성 패널 — 선택한 노드의 유형에 맞는 전용 패널로 보낸다.
 *
 * 예전에는 이 파일이 RJSF(JSON Schema 폼) 화면과 옛 노드 유형(DB추출·적재 등)의 자동 설정을 함께 들고 있었다(1,100줄).
 * 2026-10-20 모든 유형을 같은 모양의 전용 패널로 바꾸고 RJSF 를 걷어 냈다. 전용 패널이 없는 유형(옛 캔버스의 옛 유형 등)은
 * ControlNodePanel 이 이름만 고칠 수 있게 보여 준다.
 */
const ANALYTICS = new Set(['aggregate', 'sort', 'lookup', 'filter', 'join']);
const EXEC = new Set(['runWorkflow', 'shellCommand', 'sqlScript']);

const PropertyPanel: React.FC = () => {
  const { t } = useTranslation();
  const selectedNodeId = useSelector((state: any) => state.workflow.selectedNodeId);
  const nodes = useSelector((state: any) => state.workflow.nodes);
  const edges = useSelector((state: any) => state.workflow.edges);
  const currentWorkflowId = useSelector((state: any) => state.workflow.current?.id);
  const currentWorkflowName = useSelector((state: any) => state.workflow.current?.name);
  const wfSettings = useSelector((state: any) => state.workflow.settings);
  // 노드 입력·출력 데이터 조회 (최근 실행의 XCom/스테이징에서 읽는다)
  const [nodeData, setNodeData] = useState<{ dagId: string; node: NodeDataTarget; inputs: NodeDataTarget[] } | null>(null);
  const [nodeDataBusy, setNodeDataBusy] = useState(false);

  const node = useMemo(() => (selectedNodeId ? nodes.find((n: any) => n.id === selectedNodeId) || null : null), [selectedNodeId, nodes]);

  /** 선택한 노드의 입력·출력 데이터 열기 — 캔버스를 변환해 노드→태스크 매핑을 얻은 뒤 최근 실행 데이터를 읽는다 */
  const openNodeData = async () => {
    if (!currentWorkflowId || !selectedNodeId) return;
    setNodeDataBusy(true);
    try {
      const res = await be1Fetch('/api/canvas/preview', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workflow_id: currentWorkflowId, name: currentWorkflowName, nodes, edges,
                               schedule_interval: wfSettings?.schedule, settings: withTimeZone(wfSettings) }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(errorText(body, t('property:legacy.convertFailed')));
      const taskMap: Record<string, string> = body.task_map || {};
      const labelOf = (id: string) => nodes.find((n: any) => n.id === id)?.data?.label || id;
      const me: NodeDataTarget = { nodeId: selectedNodeId, label: labelOf(selectedNodeId), taskId: taskMap[selectedNodeId] || '' };
      const inputs: NodeDataTarget[] = edges
        .filter((e: any) => e.target === selectedNodeId)
        .map((e: any) => ({
          nodeId: e.source, label: labelOf(e.source), taskId: taskMap[e.source] || '',
          // 품질검사의 아래 핸들은 위반 행 — XCom 키가 따로 있다
          key: e.sourceHandle === 'reject-path' ? 'rejects' : undefined,
        }));
      setNodeData({ dagId: body.dag_id, node: me, inputs });
    } catch (e) {
      showAlert(t('property:legacy.nodeDataFailed', { message: (e as Error).message }));
    } finally {
      setNodeDataBusy(false);
    }
  };

  const content = (() => {
    if (!selectedNodeId) {
      return <div className="h-full bg-white p-4"><div className="text-gray-500 text-center mt-8">{t('workflow:property.selectNode')}</div></div>;
    }
    if (!node) return null;
    const type = String(node.type || '');
    // 분할처리로 자동 생성된 그룹 — 안쪽을 손대면 분할 규칙이 깨지므로 편집 대신 설정·해제만 제공한다
    if (node.data?.split) return <SplitGroupPanel node={node} />;
    // 그 그룹 안의 노드 — 읽기 전용. 무엇인지만 보여 주고 고치지는 못하게 한다
    if (node.data?.readOnly) {
      return (
        <div className="h-full bg-white p-4 text-sm text-gray-600">
          <div className="font-medium text-gray-800 mb-1">{node.data?.label || type}</div>
          <div className="text-xs text-gray-500 mb-3">{t('property:legacy.splitChild')}</div>
          <div className="text-xs text-gray-500">{t('property:legacy.splitChildHint')}</div>
        </div>
      );
    }
    if (type === 'transformLoad') return <TransformLoadPanel node={node} />;
    if (type === 'rdbData') return <RdbDataPanel node={node} onOpenNodeData={openNodeData} nodeDataBusy={nodeDataBusy} />;
    if (type === 'csvData') return <SamFilePanel node={node} onOpenNodeData={openNodeData} nodeDataBusy={nodeDataBusy} />;
    if (type === 'innerData') return <InnerDataPanel node={node} onOpenNodeData={openNodeData} nodeDataBusy={nodeDataBusy} />;
    if (EXEC.has(type)) return <ExecNodePanel node={node} />;
    if (type === 'subflow') return <SubflowPanel node={node} />;
    if (ANALYTICS.has(type)) return <AnalyticsNodePanel node={node} />;
    if (type === 'quality') return <QualityNodePanel node={node} />;
    if (type === 'compare') return <CompareNodePanel node={node} />;
    // 분기·합류·사용자확인·메모·반복그룹·그룹, 그리고 전용 패널이 없는 유형(이름만)
    return <ControlNodePanel node={node} />;
  })();

  return (
    <>
      {content}
      {nodeData && (
        <NodeDataModal dagId={nodeData.dagId} node={nodeData.node} inputs={nodeData.inputs} onClose={() => setNodeData(null)} />
      )}
    </>
  );
};

export default PropertyPanel;
